/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { InboundOrder, InboundPackage, useInboundOrderQuery } from '../../../services/inbound';
import Badge, { BadgeType } from '../../../components/ui/Badge/Badge';
import Overlay from '../../../components/ui/Overlay/Overlay';
import OverlayHead from '../../../components/ui/Overlay/OverlayHead';
import { InboundPackageOverlay } from '../InboundPackageOverlay/InboundPackageOverlay';
import { NotAnnouncedBadge } from '../NotAnnouncedBadge';
import { ExtraUnitsBadge } from '../ExtraUnitsBadge';
import { DataView, UsePagedQuery } from '../../../components/ui/TableOverview/DataView';
import { useDataViewController } from '../../../hooks/useDataViewController';
import { SearchModels } from '../../../components/ui/TableOverview/utils/SearchFieldRegistry';

export function InboundtatusBadge({ status }: { status: InboundOrder['inbound_state'] }) {
  const badgeType: Record<string, BadgeType> = {
    entwurf: BadgeType.Neutral2,
    zugestellt: BadgeType.Successful,
    'bereit für versand': BadgeType.Neutral4,
    'teilweise retourniert': BadgeType.Warning,
    'vollständig retourniert': BadgeType.Neutral,
    abgebrochen: BadgeType.Error,
    cancel: BadgeType.Error,
    done: BadgeType.Successful,
    in_progress: BadgeType.Warning,
    waiting: BadgeType.Neutral2,
    // Matches the "Eingecheckt" fallback colour in InboundTable's InboundStatusBadge.
    check_in: BadgeType.Neutral6,
  };

  return (
    <Badge
      className="max-w-min normal-case"
      label={status?.label ?? ''}
      expanded
      colorOverride={status?.color ? status.color : ''}
      type={status?.color ? '' : badgeType[status?.key ?? ''] ?? BadgeType.Neutral}
    />
  );
}

export interface InboundSummaryTableProps {
  inboundOrder: InboundOrder | null;
}

const InboundSummaryTable: React.FC<InboundSummaryTableProps> = ({ inboundOrder }) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<InboundPackage | null>(null);

  const controller = useDataViewController();

  const singleInboundOrderQuery = useInboundOrderQuery({
    path: { inbound_order_id: inboundOrder?.id ?? 0 },
    query: { offset: (controller.page - 1) * controller.pageSize, limit: controller.pageSize },
  });

  const pagedPackagesQuery: UsePagedQuery<InboundPackage> = () => {
    const order = singleInboundOrderQuery.data;

    if (!order) {
      return { data: undefined, isLoading: singleInboundOrderQuery.isLoading };
    }

    const packages = order.packages ?? [];

    return {
      data: {
        items: packages,
        results: packages,
        total: (order.total_pages ?? 1) * (order.limit ?? controller.pageSize),
        page: controller.page,
        size: controller.pageSize,
      },
      isLoading: singleInboundOrderQuery.isLoading,
    };
  };

  const columnHelper = createColumnHelper<InboundPackage>();

  const getInboundPackageColumns = (t: any) => {
    return [
      columnHelper.accessor(row => row.package_number, {
        id: 'package_number',
        size: 80,
        enableHiding: false,
        meta: { label: t('inbound.package_number') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => <span className="typo-text-14--bold text-full">{info.getValue()}</span>,
      }),
      columnHelper.accessor(row => row.package_type, {
        id: 'package_type',
        size: 120,
        meta: { label: t('inbound.package_type') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => (
          <span className="typo-text-14 text-full">
            {t(`inbound.${info.getValue()}`, { defaultValue: info.getValue() })}
          </span>
        ),
      }),
      columnHelper.accessor(row => row.name, {
        id: 'name',
        size: 200,
        meta: { label: t('general.reference') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => (
          <span className="flex items-center gap-x-8">
            <span className="typo-text-14--bold text-full">{info.getValue()}</span>
            <NotAnnouncedBadge announced={info.row.original.announced} />
          </span>
        ),
      }),
      columnHelper.accessor(row => row.amount_sku, {
        id: 'amount_sku',
        size: 100,
        meta: { label: t('inbound.amount_sku') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => <span className="typo-text-14 text-full">{info.getValue()}</span>,
      }),
      columnHelper.accessor(row => row.amount_units, {
        id: 'amount_units',
        size: 100,
        meta: { label: t('inbound.amount_units') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => (
          <span className="flex items-center gap-x-8">
            <span className="typo-text-14 text-full">{info.getValue()}</span>
            <ExtraUnitsBadge extra={info.row.original.extra_units} />
          </span>
        ),
      }),
      columnHelper.display({
        id: 'package_state',
        size: 150,
        meta: { label: t('inbound.state') },
        header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
        cell: info => {
          const pkg = info.row.original;

          return (
            <div className="flex">
              <span className="typo-text-14 text-full capitalize">
                <InboundtatusBadge status={pkg.package_state} />
              </span>
            </div>
          );
        },
      }),
    ];
  };

  const columns = useMemo(() => {
    if (!inboundOrder) return [];

    return getInboundPackageColumns(t);
  }, [inboundOrder, t]);

  return (
    <>
      <DataView<InboundPackage>
        controller={controller}
        modelKey={SearchModels.Package}
        noResultsLabel={t('general.no_data')}
        applyContainer={false}
        headline={undefined}
        kpiModel={undefined}
        tabs={undefined}
        displayActionsColumn={false}
        showFilters={false}
        showSearch={false}
        displayColumnSelect={false}
        filterModelId=""
        columns={columns}
        actions={[
          {
            title: t('inbound.open_package_detail'),
            icon: 'package',
            onExecute: selected => {
              setSelectedPackage(selected[0] as unknown as InboundPackage);
            },
            isRowClickAction: true,
          },
        ]}
        useDataQuery={pagedPackagesQuery}
      />
      {selectedPackage && inboundOrder && (
        <Overlay>
          <OverlayHead onCloseOverlay={() => setSelectedPackage(null)} />
          <InboundPackageOverlay
            inboundOrder={inboundOrder}
            inboundPackage={selectedPackage}
            onClose={() => setSelectedPackage(null)}
          />
        </Overlay>
      )}
    </>
  );
};

export default InboundSummaryTable;
